import './index.less';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    getFramework,
    getOperateType,
    getPointRect,
    getTargetType,
    useHoverId,
    useIdentity,
    useIsMouseSelect,
    useScale,
} from '@/framework';
import { getLocaleStore } from '@/i18n';
import { getLayerAgentOperateConfig } from '@/utils/agent-teams-kit';
import { getLayerInfoWithRotate } from '@/utils/layer-kit';
import { IExportPlugin, LayerType, PluginType } from '@/version-types';
import { resolveFormatBrushTargetId, shouldSkipHoverTarget } from '../format-brush-target';
import { getGroupInnerState } from '../pick/states/group-inner-state';
import { MouseHover } from './hover';

// NOTE(2025-12-26 @ll23): 理论 mouse-select 禁用与否对于 hover 应该没有影响
// 故暂时先注释掉 framework.operate.isDisableMouseSelect() 的判断

const Hover: React.FC = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const { $t } = getLocaleStore();
    const tipTxtMap: Record<string, string> = {
        [LayerType.TEXT_LAYER]: $t((v) => v.stage.defaultText),
        [LayerType.IMAGE_LAYER]: $t((v) => v.stage.viewImageInfo),
    };
    const [isTempLayer, setIsTempLayer] = useState(false);
    const hoverId = useHoverId();
    const scale = useScale();
    const isMouseSelect = useIsMouseSelect();
    const hoverTargetRef = useRef<HTMLDivElement>(null);

    const mouseHoverPlugin = framework.service.registration.getInstance<MouseHover>('MouseHover');
    const [doubleClickTipVisible, setDoubleClickTipVisible] = useState(mouseHoverPlugin.isDoubleClickTipVisible());
    const [formatBrushState, setFormatBrushState] = useState(framework.service.formatBrush.getState());
    const [formatBrushTargetId, setFormatBrushTargetId] = useState('');

    useEffect(() => {
        return mouseHoverPlugin.onVisibleChange((visible) => {
            setDoubleClickTipVisible(visible);
        });
    }, []);

    useEffect(() => framework.service.formatBrush.onChange(setFormatBrushState), [framework]);

    useEffect(() => {
        const handle = framework.service.editor.onMouseMove((ev) => {
            const frameOperate = framework.service.frame;
            const editor = framework.service.editor;

            if (
                editor.disablePick ||
                editor.isMouseSelect ||
                editor.disableHover ||
                editor.isBaseOperate ||
                frameOperate.getSelectedIds().size ||
                getGroupInnerState(identity).getIsActive()
            ) {
                setFormatBrushTargetId('');
                return;
            }

            const operateType = getOperateType(ev.target);
            const targetType = getTargetType(ev.target);
            if (shouldSkipHoverTarget(operateType, targetType, !!framework.service.formatBrush.getState())) {
                setFormatBrushTargetId('');
                framework.service.frame.delHoverId();
                framework.service.frame.setHoverFrameId(null);
                return;
            }

            const mPoint = { x: ev.pageX, y: ev.pageY };
            const { transform, edgelessCanvas, frame } = framework.service;
            const stageIndex = edgelessCanvas.isLongImageMode
                ? transform.getFrameIndexByMousePointY(mPoint.y)
                : frame.getActiveIndex();
            if (!transform.checkPointInFrame(mPoint, stageIndex)) {
                setFormatBrushTargetId('');
                frame.delHoverId();
                return;
            }

            const point = framework.service.transform.getScenePoint(ev);
            const pointRect = getPointRect(point);
            // 如果激活图层在鼠标范围内，则不设置 hoverId
            const activeLayer = framework.service.frame.getActiveLayer();
            if (activeLayer) {
                const layerId = framework.service.transform.tryPickWrap(pointRect, [
                    framework.service.flatLayer.getLayer(activeLayer.id),
                ]);
                if (layerId) {
                    setFormatBrushTargetId(resolveFormatBrushTargetId(framework.service, pointRect, layerId));
                    framework.service.frame.setHoverFrameId(null);
                    framework.service.frame.delHoverId();
                    return;
                }
            }
            // console.log('----------------');
            const originIndex = framework.service.frame.indexToFrameId(
                framework.service.transform.getFrameIndexByMousePointY(ev.clientY),
            );
            const layers = framework.service.frame.getCollisionLayers(originIndex, false, true);
            let layerId = framework.service.transform.tryPickWrap(pointRect, layers);
            let frameId: number | null = null;
            if (!layerId && framework.service.edgelessCanvas.isEdgelessMode) {
                // 检测是否在裁剪区域范围内
                const res = framework.service.frame.pickWithLayerId(point);
                frameId = res.frameId;
                layerId = res.layerId;
            }
            setFormatBrushTargetId(resolveFormatBrushTargetId(framework.service, pointRect, layerId || ''));
            framework.service.frame.setHoverFrameId(frameId);
            framework.service.frame.setHoverId(layerId);
        });
        return () => {
            framework.service.events.off(handle);
        };
    }, []);

    useEffect(() => {
        const layer = hoverId ? framework.service.flatLayer.getLayer(hoverId) : undefined;
        setIsTempLayer(!!layer?.__isTempLayer);

        if (!formatBrushState) {
            framework.service.formatBrush.clearHover();
            return;
        }

        if (layer) {
            framework.service.formatBrush.hover(hoverTargetRef.current, formatBrushTargetId);
        } else {
            framework.service.formatBrush.hover(null, formatBrushTargetId);
        }
        return () => framework.service.formatBrush.clearHover();
    }, [formatBrushState, formatBrushTargetId, hoverId, scale]);

    // if (!hoverId || framework.service.editor.disableMouseSelect || isTempLayer) return null;
    // if (!hoverId || isTempLayer || framework.service.editor.readonly) return null;
    if (!hoverId || isTempLayer) return null;

    const layer = framework.service.flatLayer.getLayer(hoverId);
    if (!layer) return null;
    const behavior = framework.service.behavior.get(layer);
    const customHoverOperate = behavior?.customHoverOperate?.(layer);

    const agentOperateConfig = getLayerAgentOperateConfig(layer);
    const borderRadiusStyle = !agentOperateConfig?.style?.borderRadius
        ? undefined
        : { borderRadius: agentOperateConfig.style.borderRadius * scale };

    return (
        <>
            {isMouseSelect || !doubleClickTipVisible || !tipTxtMap[layer.layerType] || layer.lock ? null : (
                <BaseStagePlugin style={{ zIndex: 20 }} name="hover-layer-tip">
                    <BaseOperateView
                        layer={{ ...getLayerInfoWithRotate(framework.service.flatLayer.convertLayer(layer)!) }}
                        offsetTop={45}
                    >
                        <div className="hover-operate__tip">{tipTxtMap[layer.layerType]}</div>
                    </BaseOperateView>
                </BaseStagePlugin>
            )}
            <BaseStagePlugin name="hover-layer" zIndex={2}>
                <BaseOperateView layer={layer}>
                    {customHoverOperate ? customHoverOperate : null}
                    <div
                        ref={hoverTargetRef}
                        data-layer-id={layer.id}
                        className={classNames('hover-operate', {
                            'is-group': framework.service.flatLayer.checkIsGroup(layer),
                            'is-lock': layer.lock,
                        })}
                        style={borderRadiusStyle}
                    ></div>
                </BaseOperateView>
            </BaseStagePlugin>
        </>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 1,
    name: 'hover-layer',
    render() {
        return <Hover />;
    },
    createInstance({ identity }) {
        return [{ key: 'MouseHover', value: new MouseHover(identity) }];
    },
};

export default exportPlugin;
